import {
  NAVIGATION_ERROR_ROUTE_NAME,
  navigationErrorRoutes,
} from "shared-ui/routes";
import { ADMIN_ROUTES } from "shared-ui/utils/admin-routes";
import { routeDiagnosticDetails } from "shared-ui/utils/route-diagnostic-details";
import { locationQueryToString } from "shared-ui/utils/router-utils";
import {
  createRouter,
  createWebHistory,
  type RouteLocationRaw,
} from "vue-router";
import { handleHotUpdate, routes } from "vue-router/auto-routes";
import { useAuthStore } from "@/stores/auth-store.ts";
import {
  leaveForPublicSite,
  resolveLegacyAgentDownloadRedirect,
} from "@/utils/legacy-download-redirect.ts";
import {
  consumePostLoginRedirect,
  isApiRedirectTarget,
  rememberPostLoginRedirect,
} from "@/utils/post-login-redirect.ts";
import { captureSkipAppDownload } from "@/utils/skip-app-download.ts";

const router = createRouter({
  history: createWebHistory(),
  strict: true,
  routes: [...routes, ...navigationErrorRoutes],
});

// For handling stale chunks between frontend updates
const CHUNK_LOAD_RELOAD_KEY = "controla_chunk_load_reload";
let isLeavingForOauth = false;

export { router };

if (import.meta.hot) {
  handleHotUpdate(router);
}

router.beforeEach(async (to): Promise<RouteLocationRaw | boolean> => {
  const legacyDownloadRedirectUrl = resolveLegacyAgentDownloadRedirect(to.path);
  if (legacyDownloadRedirectUrl) {
    leaveForPublicSite(legacyDownloadRedirectUrl);
    return false;
  }

  // Auth logic
  const authStore = useAuthStore();

  // Capture before an authenticated visit redirects past the login page.
  if (to.path === "/") {
    captureSkipAppDownload(to.query);
  }

  // Capture the app login request before an authenticated visit skips the login page.
  if (to.path === "/" && typeof to.query.client_id === "string") {
    authStore.storeOauthParameters(to.query);
  }

  // Persist deep link params across login redirect
  if (
    to.query.googleWorkspacePermissionsRequired === "true" &&
    !authStore.isAuthenticated
  ) {
    rememberPostLoginRedirect(
      `${ADMIN_ROUTES.systems}?googleWorkspacePermissionsRequired=true`,
    );
  }
  if (
    to.query.microsoft365PermissionsRequired === "true" &&
    !authStore.isAuthenticated
  ) {
    rememberPostLoginRedirect(
      `${ADMIN_ROUTES.systems}?microsoft365PermissionsRequired=true`,
    );
  }
  // Backend redirects unauthenticated browser navigations (e.g. OAuth
  // integration starts from an agent-opened browser) to login with the
  // original API path in `continue` so the flow resumes after login.
  if (typeof to.query.continue === "string" && !authStore.isAuthenticated) {
    rememberPostLoginRedirect(to.query.continue);
  }

  // Skip auth check during logout to prevent 401-triggered redirect loops
  if (to.query["logout"] !== "true") {
    await authStore.checkAuth(false);
  }

  const pendingOauthParams = authStore.getOauthParameters;
  if (pendingOauthParams && authStore.isAuthenticated) {
    consumePostLoginRedirect();
    authStore.resetOauthParameters();
    const oauthUrl =
      "/oauth2/authorize?" + locationQueryToString(pendingOauthParams);

    const oauthWindow = window.open(oauthUrl, "_blank");
    if (oauthWindow === null) {
      // Mobile browsers may block a popup after the asynchronous auth check.
      isLeavingForOauth = true;
      window.location.replace(oauthUrl);
      return false;
    }
    oauthWindow.focus();
  }

  const isAuthenticated = authStore.isAuthenticated;
  const isAdministrator = authStore.isAdministrator;
  const hasRegistered = authStore.hasRegistered;

  // App setup continues to the remembered destination once it finishes.
  if (isAuthenticated && !to.path.startsWith("/setup-device")) {
    const postLoginRedirect = consumePostLoginRedirect();
    if (postLoginRedirect) {
      // API targets (e.g. OAuth initiations) are backend redirects and need a
      // full page load; the router cannot resolve them.
      if (isApiRedirectTarget(postLoginRedirect)) {
        window.location.assign(postLoginRedirect);
        return false;
      }
      return postLoginRedirect;
    }
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);

  if (
    !to.path.startsWith("/registration") &&
    // Allow OAuth callback flows (e.g. gwsconnected/m365connected) to complete
    !to.path.startsWith(ADMIN_ROUTES.systems) &&
    !to.path.startsWith("/integrations") &&
    isAuthenticated &&
    isAdministrator &&
    !hasRegistered &&
    // Finishing registration is the user's own step, so impersonation must not trigger it.
    !authStore.isImpersonating
  ) {
    return { name: "/registration/download-app" };
  }

  if (to.path === "/" && isAuthenticated && hasRegistered) {
    return "/welcome";
  }

  if (requiresAuth && !isAuthenticated) {
    rememberPostLoginRedirect(to.fullPath);

    return {
      path: "/",
      query: {
        login: "true", // forces login mode
        msg: "Please login to continue.",
      },
    };
  }

  if (requiresAdmin && !isAdministrator) {
    return "/tasks";
  }

  return true;
});

router.afterEach((_to, _from, failure) => {
  if (!failure) {
    sessionStorage.removeItem(CHUNK_LOAD_RELOAD_KEY);
  }
});

router.onError((error, to, from) => {
  if (isLeavingForOauth) {
    return;
  }

  console.error("Navigation failed", error, {
    to: routeDiagnosticDetails(to),
    from: routeDiagnosticDetails(from),
  });

  if (
    isDynamicImportLoadError(error) &&
    reloadOnceForFreshChunks(to.fullPath)
  ) {
    return;
  }

  if (String(to.name) === NAVIGATION_ERROR_ROUTE_NAME) {
    return;
  }

  const code = getNavigationErrorCode(error);
  void router.replace({
    path: `/error/${code}`,
    query: { path: to.fullPath },
  });
});

function getNavigationErrorCode(error: unknown): number {
  if (!error || typeof error !== "object") {
    return 500;
  }

  const status = [
    (error as { status?: unknown }).status,
    (error as { statusCode?: unknown }).statusCode,
    (error as { response?: { status?: unknown } }).response?.status,
  ].find((value): value is number => typeof value === "number");

  if (status && status >= 400 && status <= 599) {
    return status;
  }

  return 500;
}

function isDynamicImportLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  return (
    message.includes("error loading dynamically imported module") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed")
  );
}

function reloadOnceForFreshChunks(targetPath: string): boolean {
  const reloadKey = `${window.location.origin}${targetPath}`;

  if (sessionStorage.getItem(CHUNK_LOAD_RELOAD_KEY) === reloadKey) {
    return false;
  }

  sessionStorage.setItem(CHUNK_LOAD_RELOAD_KEY, reloadKey);
  window.location.assign(targetPath);
  return true;
}
